import React, { useState, useEffect, useMemo } from 'react'
import { apiClient } from '@/utils/api'
import { useAuth } from '@/hooks/useAuth'
import { useFamily } from '@/contexts/FamilyContext'
import toast from 'react-hot-toast'
import { WeeklyView, MonthlyView } from './FamilyScheduleViews'
import { FamilyEvents } from './FamilyEvents'
import { ParentChildAvailabilityEnhanced } from './ParentChildAvailabilityEnhanced'
import { AvailabilityCalendar } from './AvailabilityCalendar'
import CalendarNavigation from './CalendarNavigation'
import { SchoolCalendarView } from './SchoolCalendarView'
import { ParentSchoolManagement } from './ParentSchoolManagement'
import { CalendarSelector } from './CalendarSelector'
import { startOfWeek, startOfMonth, addDays, format } from 'date-fns'
import { useDemoMode } from './DemoModeBanner'
import { extractDateInTimezoneSync } from '@/utils/timezoneUtils'
import { useCustodyParents, custodyLabel } from '@/hooks/useCustodyParents'

interface DailySchedule {
  date: string
  weekday: string
  day_type: string
  presence: { [key: string]: boolean }
  kids_present: string[]
  total_kids_present: number
  // Every one of the family's kids is home that day (derived client-side)
  all_kids_present?: boolean
}

interface WeekendPattern {
  week: number
  dates: {
    saturday: string
    sunday: string
  }
  presence: {
    saturday: { [key: string]: boolean }
    sunday: { [key: string]: boolean }
  }
  all_kids_present: boolean
}

interface ScheduleSummary {
  summary_period: {
    start_date: string
    end_date: string
    total_days: number
  }
  statistics: {
    // Days each kid is home, keyed by username
    kid_days: Record<string, number>
    no_kids_days: number
    school_nights: number
  }
  daily_schedules: DailySchedule[]
  weekend_pattern: WeekendPattern[]
}

interface KidEvent {
  event_name: string
  start_date: string
  end_date: string
  event_type: 'school_activity' | 'sports' | 'at_moms' | 'other'
  description?: string
  needs_approval: boolean
}

interface GoogleCalendarEvent {
  id: string
  summary: string
  start: {
    dateTime?: string
    date?: string
  }
  end: {
    dateTime?: string
    date?: string
  }
  description?: string
  colorId?: string
}

interface FamilyScheduleFixedProps {
  initialTab?: 'calendar' | 'patterns' | 'summary' | 'events' | 'availability' | 'school'
}

/**
 * Fills in what the schedule endpoints leave out: `presence` (username ->
 * home) is derived from `kids_present` when the row has no presence map.
 */
function normalizeDay(day: any): DailySchedule {
  const kids: string[] = Array.isArray(day?.kids_present) ? day.kids_present : []
  const presence =
    day?.presence && typeof day.presence === 'object' && Object.keys(day.presence).length > 0
      ? day.presence
      : Object.fromEntries(kids.map(k => [k, true]))
  return {
    ...day,
    kids_present: kids,
    total_kids_present: typeof day?.total_kids_present === 'number' ? day.total_kids_present : kids.length,
    presence
  }
}

export default function FamilyScheduleFixed({ initialTab = 'calendar' }: FamilyScheduleFixedProps = {}) {
  const { getChildren } = useFamily()
  const { isDemoMode } = useDemoMode()
  const custodyParents = useCustodyParents()

  // Get family members from context and map to expected format
  const familyMembers = getChildren().map(member => ({
    username: member.username,
    display_name: member.display_name,
    age: member.age || 0,
    color: member.profile_color || member.color_theme,
    emoji: member.emoji || '👤'
  }))
  const childUsernamesKey = familyMembers.map(m => m.username).join(',')

  // True when every one of this family's kids is in the day's kids_present.
  const allKidsHome = (kids: string[] | null | undefined) =>
    familyMembers.length > 0 && familyMembers.every(m => (kids || []).includes(m.username))
  const [activeTab, setActiveTab] = useState<'calendar' | 'patterns' | 'summary' | 'events' | 'availability' | 'school'>(initialTab)
  const [weekendPattern, setWeekendPattern] = useState<WeekendPattern[]>([])
  const [dailySchedules, setDailySchedules] = useState<DailySchedule[]>([])
  const [scheduleSummary, setScheduleSummary] = useState<ScheduleSummary | null>(null)
  const [showCustodySchedule, setShowCustodySchedule] = useState(true) // Default to true, will be loaded from settings
  const [loadedDateRange, setLoadedDateRange] = useState<{start: Date, end: Date} | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState('')
  const [calendarView, setCalendarView] = useState<'day' | 'daily' | 'weekly' | 'monthly' | 'agenda'>('weekly')
  const [selectedKid, setSelectedKid] = useState<string | undefined>(undefined)
  // Always use current date, not cached
  const [viewDate, setViewDate] = useState(() => {
    const now = new Date()
    // Initialize with current date
    return now
  })
  const [newEvent, setNewEvent] = useState<KidEvent>({
    event_name: '',
    start_date: '',
    end_date: '',
    event_type: 'other',
    description: '',
    needs_approval: true
  })
  const [googleCalendarEvents, setGoogleCalendarEvents] = useState<GoogleCalendarEvent[]>([])
  const [selectedCalendar, setSelectedCalendar] = useState<string>('')
  const [selectedCalendars, setSelectedCalendars] = useState<string[]>(['family-schedule', 'family-holidays'])
  const [availableCalendars, setAvailableCalendars] = useState<any[]>([])
  const [schoolDistrictEvents, setSchoolDistrictEvents] = useState<any[]>([])
  const { user } = useAuth()

  useEffect(() => {
    // Only load calendar settings for parents (admin endpoint)
    if (user?.is_parent) {
      loadCalendarSettings() // Load calendar settings first to check custody toggle
    }
    loadScheduleData()
    loadCalendarEvents() // Load school events on mount
  }, [user])

  // all_kids_present depends on the family's kid list, which can arrive after
  // the schedule, so it is derived here rather than stored at load time.
  const schedulesWithFlags = useMemo(
    () => dailySchedules.map(d => ({ ...d, all_kids_present: allKidsHome(d.kids_present) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dailySchedules, childUsernamesKey]
  )

  const summaryStats = useMemo(() => {
    const days = scheduleSummary?.daily_schedules || []
    const allKidsDays = days.filter(d => allKidsHome(d.kids_present))
    const allKidsWeekendDays = allKidsDays.filter(d => {
      const weekday = String(d.weekday || '').toLowerCase()
      return weekday === 'saturday' || weekday === 'sunday'
    })
    return { allKidsDays: allKidsDays.length, allKidsWeekendDays: allKidsWeekendDays.length }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleSummary, childUsernamesKey])

  const loadCalendarSettings = async () => {
    try {
      const settings = await apiClient.get('/api/admin/settings/calendar')
      // Check if custody schedule should be shown
      setShowCustodySchedule(settings.show_custody_schedule !== false) // Default to true if not set
    } catch (error) {
      console.error('Failed to load calendar settings:', error)
      // If the settings call fails, keep custody visible for a family that
      // has named its custody parents so the Patterns and Stats tabs stay up.
      setShowCustodySchedule(custodyParents.configured)
    }
  }

  useEffect(() => {
    // Reload schedule data when view changes to ensure we have enough data
    if (calendarView === 'monthly') {
      loadScheduleData(60) // Load 60 days for monthly view
    }
  }, [calendarView])

  const loadScheduleData = async (daysToLoad: number = 90) => {
    setIsLoading(true)
    try {
      // Load weekend pattern for next 8 weeks (legacy endpoint, may not exist in Go backend)
      try {
        const weekends = await apiClient.getWeekendPattern(8)
        setWeekendPattern(weekends)
      } catch (error) {
        console.warn('Weekend pattern endpoint not available:', error)
        setWeekendPattern([])
      }

      // Load daily schedules - include past 30 days and future days
      const pastDays = 30 // Show 30 days in the past
      const startDate = extractDateInTimezoneSync(new Date(Date.now() - pastDays * 24 * 60 * 60 * 1000).toISOString())
      const endDate = extractDateInTimezoneSync(new Date(Date.now() + daysToLoad * 24 * 60 * 60 * 1000).toISOString())
      
      // Track the loaded date range
      setLoadedDateRange({
        start: new Date(startDate + 'T12:00:00'),
        end: new Date(endDate + 'T12:00:00')
      })
      
      const scheduleRange = await apiClient.getScheduleRange(startDate, endDate)

      // Handle null or empty response from schedule API (e.g., for demo families without custody data)
      if (scheduleRange && scheduleRange.schedule) {
        setDailySchedules(scheduleRange.schedule.map(normalizeDay))
      } else {
        setDailySchedules([])
      }
      
      // Load schedule summary
      const summaryData = await apiClient.getScheduleSummary(30)

      // Transform backend response to match expected frontend interface
      if (summaryData) {
        // Per-kid counts for whichever kids appear in family_presence. The
        // all-kids figures depend on the family's kid list, which can load
        // after this, so they are derived at render time (summaryStats).
        const presenceDays: any[] = Array.isArray(summaryData.family_presence) ? summaryData.family_presence : []
        const kidPresenceCounts: Record<string, number> = {}
        presenceDays.forEach((day: any) => {
          const kids: string[] = Array.isArray(day.kids_present) ? day.kids_present : []
          kids.forEach((kid: string) => {
            kidPresenceCounts[kid] = (kidPresenceCounts[kid] || 0) + 1
          })
        })

        const transformed: ScheduleSummary = {
          summary_period: {
            start_date: summaryData.date_range?.start_date || '',
            end_date: summaryData.date_range?.end_date || '',
            total_days: summaryData.date_range?.days || 0
          },
          statistics: {
            kid_days: kidPresenceCounts,
            no_kids_days: summaryData.custody_breakdown?.no_kids_days || 0,
            school_nights: summaryData.custody_breakdown?.split_custody_days || 0
          },
          daily_schedules: presenceDays.map(normalizeDay),
          weekend_pattern: []
        }
        setScheduleSummary(transformed)
      }

      // Load Google Calendars if user is authenticated with Google
      if (user?.google_auth) {
        try {
          const calendars = await apiClient.getGoogleCalendars()
          setAvailableCalendars(calendars.calendars || [])
          
          // Load events from the first calendar (usually primary)
          if (calendars.calendars && calendars.calendars.length > 0) {
            const primaryCalendar = calendars.calendars.find((cal: any) => cal.primary) || calendars.calendars[0]
            setSelectedCalendar(primaryCalendar.id)
            // TODO: Load events from selected calendar
          }
        } catch (error) {
          console.error('Failed to load Google Calendar data:', error)
        }
      }
    } catch (error: any) {
      console.error('Failed to load schedule data:', error)
      toast.error('Failed to load family schedule')
    }
    setIsLoading(false)
  }

  const loadCalendarEvents = async () => {
    try {
      // Load ALL school events from unified endpoint
      const events = await apiClient.get('/api/schools/events')

      // Map to calendar format with additional metadata
      const formattedEvents = (Array.isArray(events) ? events : []).map((e: any) => ({
        ...e,
        calendar_type: 'school',
        event_date: e.date, // Map 'date' to 'event_date' for compatibility
        display_name: e.name,
        school_name: e.school,
        event_type_label: e.type
      }))

      setSchoolDistrictEvents(formattedEvents)
    } catch (error) {
      console.error('Failed to load calendar events:', error)
      setSchoolDistrictEvents([])
    }
  }

  const handleCreateEvent = async () => {
    if (!newEvent.event_name || !newEvent.start_date || !newEvent.end_date) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      await apiClient.createMyEvent(newEvent)
      toast.success(newEvent.needs_approval ? 'Event submitted for approval!' : 'Event created!')
      
      // Reset form
      setNewEvent({
        event_name: '',
        start_date: '',
        end_date: '',
        event_type: 'other',
        description: '',
        needs_approval: true
      })
      
      // Reload data
      loadScheduleData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create event')
    }
  }

  const formatDate = (dateString: string) => {
    // Parse as local date, not UTC to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  const formatDateLong = (dateString: string) => {
    // Parse as local date, not UTC to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getWeekendType = (weekend: WeekendPattern) => {
    // Count how many kids are present on Saturday and Sunday
    const saturdayKids = Object.values(weekend.presence.saturday)
    const sundayKids = Object.values(weekend.presence.sunday)
    const saturdayPresent = saturdayKids.filter(present => present).length
    const sundayPresent = sundayKids.filter(present => present).length

    // Every kid present both days: the household parent's weekend
    if (saturdayKids.length > 0 && saturdayPresent === saturdayKids.length &&
        sundayKids.length > 0 && sundayPresent === sundayKids.length) {
      return 'primary'
    }
    // No kids present both days: the other parent's weekend
    if (saturdayPresent === 0 && sundayPresent === 0) {
      return 'other'
    }
    // Otherwise it's a split weekend
    return 'split'
  }

  const getWeekendTypeLabel = (weekend: WeekendPattern) => {
    const type = getWeekendType(weekend)
    switch(type) {
      case 'primary':
        return `🏠 ${custodyParents.primary}'s Weekend`
      case 'other':
        return `✈️ ${custodyParents.other}'s Weekend`
      default:
        return "🔄 Split Weekend"
    }
  }

  const getWeekendTypeStyle = (weekend: WeekendPattern) => {
    const type = getWeekendType(weekend)
    switch(type) {
      case 'primary':
        return 'bg-blue-200 text-blue-800'
      case 'other':
        return 'bg-pink-200 text-pink-800'
      default:
        return 'bg-yellow-200 text-yellow-800'
    }
  }

  const getDayTypeColor = (dayType: string) => {
    switch (dayType) {
      case 'all_kids_weekend': return 'bg-green-100 text-green-800 border-green-200'
      case 'no_kids_weekend': return 'bg-gray-100 text-gray-600 border-gray-200'
      case 'school_night': return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'weekday': return 'bg-white border-gray-200'
      case 'partial_weekend': return 'bg-orange-100 text-orange-800 border-orange-200'
      default: return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    }
  }

  const getDayTypeLabel = (dayType: string) => {
    switch (dayType) {
      case 'all_kids_weekend': return 'All Kids Weekend 🎉'
      case 'no_kids_weekend': return 'Quiet Weekend 😴'
      case 'school_night': return 'School Night 📚'
      case 'weekday': return 'Regular Day'
      default: return dayType
    }
  }

  const getDayTypeEmoji = (dayType: string) => {
    switch (dayType) {
      case 'all_kids_weekend': return '🎉'
      case 'no_kids_weekend': return '😴'
      case 'school_night': return '📚'
      default: return '📅'
    }
  }

  const getPresenceText = (isPresent: boolean) => {
    return isPresent ? 'Home' : 'Away'
  }

  const getPresenceEmoji = (isPresent: boolean) => {
    return isPresent ? '🏠' : '✈️'
  }

  // Show kid-friendly view for non-parents
  if (!user?.is_parent) {
    return (
      <div className="space-y-4">
        {/* Compact header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center">
            📅 Family Calendar
          </h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="grid grid-cols-2 gap-1 bg-gray-100 p-1 rounded-lg sm:flex sm:space-x-1">
          <button
            onClick={() => setActiveTab('calendar')}
            className={`py-2 px-2 sm:px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'calendar'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📅 Schedule
          </button>
          <button
            onClick={() => setActiveTab('school')}
            className={`py-2 px-2 sm:px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'school'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🏫 School
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`py-2 px-2 sm:px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'events'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🎯 Events
          </button>
          <button
            onClick={() => setActiveTab('availability')}
            className={`py-2 px-2 sm:px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'availability'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📆 My Availability
          </button>
        </div>

        {/* Calendar Tab Content */}
        {activeTab === 'calendar' && (
          <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center">
              📅 {calendarView === 'daily' ? 'Today' :
                  calendarView === 'weekly' ? 'Week View' : 
                  calendarView === 'monthly' ? format(viewDate, 'MMMM yyyy') :
                  calendarView === 'agenda' ? 'Upcoming Schedule' : 'Calendar'}
            </h3>
            <div className="grid grid-cols-4 gap-1 sm:flex sm:items-center sm:space-x-2">
              <button
                onClick={() => {
                  setCalendarView('daily')
                  setViewDate(new Date())
                }}
                className={`px-2 py-1 text-xs sm:px-3 sm:text-sm rounded-lg transition-colors ${
                  calendarView === 'daily'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Today
              </button>
              <button
                onClick={() => setCalendarView('weekly')}
                className={`px-2 py-1 text-xs sm:px-3 sm:text-sm rounded-lg transition-colors ${
                  calendarView === 'weekly'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Week
              </button>
              <button
                onClick={() => setCalendarView('monthly')}
                className={`px-2 py-1 text-xs sm:px-3 sm:text-sm rounded-lg transition-colors ${
                  calendarView === 'monthly'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setCalendarView('agenda')}
                className={`px-2 py-1 text-xs sm:px-3 sm:text-sm rounded-lg transition-colors ${
                  calendarView === 'agenda'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Agenda
              </button>
            </div>
          </div>
          
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <span className="text-gray-600 mt-2 block">Loading schedule...</span>
            </div>
          ) : (
            <>
              {/* Today View */}
              {calendarView === 'daily' && (
                <div className="space-y-3">
                  {(() => {
                    const today = format(new Date(), 'yyyy-MM-dd')
                    const todaySchedule = dailySchedules.find(d => d.date === today)
                    
                    if (!todaySchedule) {
                      return <div className="text-center py-8 text-gray-500">No schedule data for today</div>
                    }
                    
                    return (
                      <div className="bg-white rounded-lg shadow-lg p-6">
                        <h3 className="text-xl font-bold mb-4">{format(new Date(), 'EEEE, MMMM d, yyyy')}</h3>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <span className="text-lg font-semibold">Kids at home:</span>
                            <span className="text-2xl font-bold text-blue-600">
                              {todaySchedule.total_kids_present}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            {familyMembers.map((member) => (
                              <div
                                key={member.username}
                                className={`p-3 rounded-lg border-2 ${
                                  todaySchedule.presence[member.username]
                                    ? 'bg-green-50 border-green-300'
                                    : 'bg-gray-50 border-gray-200'
                                }`}
                              >
                                <div className="flex items-center space-x-2">
                                  <span className="text-2xl">{member.emoji}</span>
                                  <div>
                                    <div className="font-semibold">{member.display_name}</div>
                                    <div className="text-sm text-gray-600">
                                      {todaySchedule.presence[member.username] ? '🏠 Home' : '❌ Away'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}

              {/* Week View */}
              {calendarView === 'weekly' && (
                <WeeklyView
                  dailySchedules={schedulesWithFlags}
                  currentDate={viewDate}
                  onDateChange={(date) => {
                    setViewDate(date)
                    // Check if we need to reload data when navigating
                    if (loadedDateRange) {
                      const bufferDays = 7 * 24 * 60 * 60 * 1000
                      const needsReload = date < new Date(loadedDateRange.start.getTime() + bufferDays) ||
                                         date > new Date(loadedDateRange.end.getTime() - bufferDays)
                      if (needsReload) {
                        const daysFromToday = Math.ceil((date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                        const daysToLoad = Math.max(90, Math.abs(daysFromToday) + 30)
                        loadScheduleData(daysToLoad)
                      }
                    }
                  }}
                  familyMembers={familyMembers}
                  calendarEvents={schoolDistrictEvents}
                />
              )}

              {/* Month View */}
              {calendarView === 'monthly' && (
                <MonthlyView
                  dailySchedules={schedulesWithFlags}
                  currentDate={viewDate}
                  onDateChange={(date) => {
                    setViewDate(date)
                    // Check if we need to reload data when navigating
                    if (loadedDateRange) {
                      const bufferDays = 7 * 24 * 60 * 60 * 1000
                      const needsReload = date < new Date(loadedDateRange.start.getTime() + bufferDays) ||
                                         date > new Date(loadedDateRange.end.getTime() - bufferDays)
                      if (needsReload) {
                        const daysFromToday = Math.ceil((date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                        const daysToLoad = Math.max(90, Math.abs(daysFromToday) + 30)
                        loadScheduleData(daysToLoad)
                      }
                    }
                  }}
                  familyMembers={familyMembers}
                  calendarEvents={schoolDistrictEvents}
                />
              )}

              {/* Agenda View - List style */}
              {calendarView === 'agenda' && (
                <div className="space-y-3">
                  {(() => {
                    // Find the start of current week (Sunday) and show current + next week (14 days)
                    const today = new Date()
                    const dayOfWeek = today.getDay() // 0 = Sunday, 6 = Saturday
                    const startOfWeek = new Date(today)
                    startOfWeek.setDate(today.getDate() - dayOfWeek) // Go back to Sunday
                    const startOfWeekStr = `${startOfWeek.getFullYear()}-${String(startOfWeek.getMonth() + 1).padStart(2, '0')}-${String(startOfWeek.getDate()).padStart(2, '0')}`
                    
                    const weekStartIndex = dailySchedules.findIndex(d => d.date >= startOfWeekStr)
                    const startIndex = weekStartIndex >= 0 ? weekStartIndex : 0
                    
                    // Show 14 days (current week + next week)
                    return dailySchedules.slice(startIndex, startIndex + 14).map((day) => (
                  <div key={day.date} className={`p-4 rounded-lg border-2 ${getDayTypeColor(day.day_type)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{getDayTypeEmoji(day.day_type)}</span>
                        <span className="font-semibold">{formatDate(day.date)}</span>
                    </div>
                    <span className="text-sm font-medium">
                      {day.total_kids_present} kids home
                    </span>
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {familyMembers.map((member) => (
                        <div
                          key={member.username}
                          className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                            day.presence[member.username]
                              ? 'bg-green-200 text-green-800'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          <span>{member.emoji}</span>
                          <span className="max-w-[60px] truncate">{member.display_name}</span>
                          <span>{getPresenceEmoji(day.presence[member.username])}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                    </div>
                  ))
                })()}
              </div>
            )}
          </>
        )}
        </div>
        )}
        
        
        {/* Events Tab */}
        {activeTab === 'events' && (
          <FamilyEvents />
        )}
        
        {/* Availability Tab */}
        {activeTab === 'availability' && (
          <AvailabilityCalendar />
        )}
        
        {/* School Tab - Kids see their school calendar */}
        {activeTab === 'school' && (
          <SchoolCalendarView />
        )}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Loading family schedule...</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Compact Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center">
          📅 Master Calendar
        </h2>
        {scheduleSummary?.statistics && (
          <div className="flex items-center space-x-3 text-sm">
            <span className="text-gray-600">
              <span className="font-semibold text-green-600">{summaryStats.allKidsDays}</span> all-kids days
            </span>
            <span className="text-gray-400">|</span>
            <span className="text-gray-600">
              <span className="font-semibold text-blue-600">{scheduleSummary.statistics.school_nights}</span> school nights
            </span>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('calendar')}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'calendar'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          📅 Calendar
        </button>
        <button
          onClick={() => setActiveTab('events')}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'events'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          🎯 Events
        </button>
        <button
          onClick={() => setActiveTab('availability')}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'availability'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          📆 Availability
        </button>
        {/* Only show custody-related tabs if custody tracking is enabled (via settings) AND not in demo mode AND custody data exists */}
        {showCustodySchedule && !isDemoMode && weekendPattern.length > 0 && (
          <button
            onClick={() => setActiveTab('patterns')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'patterns'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🔄 Patterns
          </button>
        )}
        {showCustodySchedule && !isDemoMode && (
          <button
            onClick={() => setActiveTab('summary')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'summary'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📊 Stats
          </button>
        )}
      </div>

      {/* Calendar View Tab */}
      {activeTab === 'calendar' && (
        <div className="card">
            
            {/* Integrated Calendar Navigation */}
            <CalendarNavigation 
              currentDate={viewDate}
              onDateChange={(date) => {
                setViewDate(date)
                
                // Check if the new date is outside our loaded data range
                if (loadedDateRange) {
                  // Add a 7-day buffer before the edge to preload data
                  const bufferDays = 7 * 24 * 60 * 60 * 1000
                  const needsReload = date < new Date(loadedDateRange.start.getTime() + bufferDays) || 
                                     date > new Date(loadedDateRange.end.getTime() - bufferDays)
                  
                  if (needsReload) {
                    // Calculate days from today to load appropriate amount
                    const daysFromToday = Math.ceil((date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                    const daysToLoad = Math.max(60, Math.abs(daysFromToday) + 30) // Load at least 60 days or enough to cover the target date + buffer
                    loadScheduleData(daysToLoad)
                  }
                }
              }}
              view={calendarView}
              selectedKid={selectedKid}
              onKidFilterChange={setSelectedKid}
              familyMembers={familyMembers}
            />
            
            {/* Calendar Selector and View type selector */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mt-4">
              {/* Calendar Selector */}
              <div className="w-full lg:w-auto">
                <CalendarSelector
                  selectedCalendars={selectedCalendars}
                  onCalendarToggle={(calendarId) => {
                    setSelectedCalendars(prev => 
                      prev.includes(calendarId)
                        ? prev.filter(id => id !== calendarId)
                        : [...prev, calendarId]
                    )
                  }}
                  onCalendarsChange={setSelectedCalendars}
                  multiSelect={true}
                  showTypes={['district', 'school', 'family']}
                  className="max-w-xs"
                />
              </div>
              
              {/* View type selector */}
              <div className="grid grid-cols-3 gap-1 sm:grid-cols-5 sm:flex sm:space-x-2">
              <button
                onClick={() => setCalendarView('day')}
                className={`px-2 py-1 text-xs sm:px-3 sm:text-sm rounded-md ${
                  calendarView === 'day'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Day
              </button>
              <button
                onClick={() => setCalendarView('daily')}
                className={`px-2 py-1 text-xs sm:px-3 sm:text-sm rounded-md ${
                  calendarView === 'daily'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                3-Day
              </button>
              <button
                onClick={() => setCalendarView('weekly')}
                className={`px-2 py-1 text-xs sm:px-3 sm:text-sm rounded-md ${
                  calendarView === 'weekly'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Week
              </button>
              <button
                onClick={() => setCalendarView('monthly')}
                className={`px-2 py-1 text-xs sm:px-3 sm:text-sm rounded-md ${
                  calendarView === 'monthly'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setCalendarView('agenda')}
                className={`px-2 py-1 text-xs sm:px-3 sm:text-sm rounded-md ${
                  calendarView === 'agenda'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Agenda
              </button>
              </div>
            </div>
          
          {calendarView === 'day' ? (
            // Single Day View - Shows only the current view date with full details
            <div className="max-w-lg mx-auto">
              {(() => {
                const viewDateStr = extractDateInTimezoneSync(viewDate.toISOString())
                const daySchedule = dailySchedules.find(d => d.date === viewDateStr)
                const today = extractDateInTimezoneSync(new Date().toISOString())
                const isToday = viewDateStr === today
                
                if (!daySchedule) {
                  return (
                    <div className="card text-center py-8">
                      <p className="text-gray-500">No schedule data for this date</p>
                    </div>
                  )
                }
                
                return (
                  <div className={`card border-2 ${isToday ? 'ring-2 ring-blue-500' : ''} ${getDayTypeColor(daySchedule.day_type)}`}>
                    <div className="mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-2xl font-bold text-gray-900">
                            {formatDateLong(daySchedule.date)}
                          </div>
                        </div>
                        {isToday && (
                          <span className="bg-blue-500 text-white text-sm px-3 py-1 rounded-full">
                            TODAY
                          </span>
                        )}
                      </div>
                      <div className="text-xl mt-2">{getDayTypeEmoji(daySchedule.day_type)} {getDayTypeLabel(daySchedule.day_type)}</div>
                    </div>
                    
                    <div className="space-y-3 mb-4">
                      <div className="font-medium text-gray-700">Kids Present:</div>
                      <div className="grid grid-cols-2 gap-3">
                        {familyMembers.map((member) => (
                          <div
                            key={member.username}
                            className={`flex items-center space-x-2 p-3 rounded-lg ${
                              daySchedule.presence[member.username]
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            <span className="text-xl">{member.emoji}</span>
                            <span className="font-medium">{member.display_name}</span>
                            <span className="ml-auto text-lg">{getPresenceEmoji(daySchedule.presence[member.username])}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {daySchedule.notes && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                        <div className="text-sm font-medium text-yellow-800">Notes:</div>
                        <div className="text-sm text-yellow-700">{daySchedule.notes}</div>
                      </div>
                    )}
                    
                    <div className="text-center pt-4 border-t">
                      <div className="text-3xl font-bold text-gray-800">
                        {daySchedule.total_kids_present}
                      </div>
                      <div className="text-gray-600">kids home</div>
                    </div>
                  </div>
                )
              })()}
            </div>
          ) : calendarView === 'daily' ? (
            // 3-Day View - Current view date and next 2 days with full details
            <div className="grid gap-4 md:grid-cols-3">
              {(() => {
                const viewDateStr = extractDateInTimezoneSync(viewDate.toISOString())
                const viewIndex = dailySchedules.findIndex(d => d.date >= viewDateStr)
                const daysToShow = dailySchedules.slice(viewIndex, viewIndex + 3)
                const today = extractDateInTimezoneSync(new Date().toISOString())

                return daysToShow
                  .filter(day => {
                    // Filter by selected kid if applicable
                    if (!selectedKid) return true
                    return day.presence[selectedKid]
                  })
                  .map((day) => {
                    const isToday = day.date === today
                    return (
                      <div 
                        key={day.date} 
                        className={`card border-2 ${isToday ? 'ring-2 ring-blue-500' : ''} ${getDayTypeColor(day.day_type)}`}
                      >
                        <div className="mb-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xl font-bold text-gray-900">
                                {formatDateLong(day.date)}
                              </div>
                            </div>
                            {isToday && (
                              <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                                TODAY
                              </span>
                            )}
                          </div>
                          <div className="text-lg mt-2">{getDayTypeEmoji(day.day_type)} {getDayTypeLabel(day.day_type)}</div>
                        </div>
                        
                        <div className="space-y-2 mb-4">
                          <div className="font-medium text-sm text-gray-700">Kids Present:</div>
                          <div className="grid grid-cols-2 gap-2">
                            {familyMembers.map((member) => (
                              <div
                                key={member.username}
                                className={`flex items-center space-x-2 p-2 rounded-lg ${
                                  day.presence[member.username]
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-500'
                                }`}
                              >
                                <span className="text-lg">{member.emoji}</span>
                                <span className="font-medium">{member.display_name}</span>
                                <span className="ml-auto">{getPresenceEmoji(day.presence[member.username])}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {day.notes && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3">
                            <div className="text-xs font-medium text-yellow-800">Notes:</div>
                            <div className="text-sm text-yellow-700">{day.notes}</div>
                          </div>
                        )}
                        
                        <div className="text-center pt-3 border-t">
                          <div className="text-2xl font-bold text-gray-800">
                            {day.total_kids_present}
                          </div>
                          <div className="text-sm text-gray-600">kids home</div>
                        </div>
                      </div>
                    )
                  })
              })()}
            </div>
          ) : calendarView === 'weekly' ? (
            <WeeklyView
              dailySchedules={schedulesWithFlags
                .filter(day => {
                  // Filter by selected kid if applicable
                  if (!selectedKid) return true
                  return day.presence[selectedKid]
                })}
              currentDate={viewDate}
              onDateChange={(date) => {
                setViewDate(date)
                // Check if we need to reload data when navigating
                if (loadedDateRange) {
                  const bufferDays = 7 * 24 * 60 * 60 * 1000
                  const needsReload = date < new Date(loadedDateRange.start.getTime() + bufferDays) ||
                                     date > new Date(loadedDateRange.end.getTime() - bufferDays)
                  if (needsReload) {
                    const daysFromToday = Math.ceil((date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                    const daysToLoad = Math.max(90, Math.abs(daysFromToday) + 30)
                    loadScheduleData(daysToLoad)
                  }
                }
              }}
              familyMembers={familyMembers}
              calendarEvents={schoolDistrictEvents}
            />
          ) : calendarView === 'monthly' ? (
            <MonthlyView
              dailySchedules={schedulesWithFlags
                .filter(day => {
                  // Filter by selected kid if applicable
                  if (!selectedKid) return true
                  return day.presence[selectedKid]
                })}
              currentDate={viewDate}
              onDateChange={(date) => {
                setViewDate(date)
                // Check if we need to reload data when navigating
                if (loadedDateRange) {
                  const bufferDays = 7 * 24 * 60 * 60 * 1000
                  const needsReload = date < new Date(loadedDateRange.start.getTime() + bufferDays) ||
                                     date > new Date(loadedDateRange.end.getTime() - bufferDays)
                  if (needsReload) {
                    const daysFromToday = Math.ceil((date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                    const daysToLoad = Math.max(90, Math.abs(daysFromToday) + 30)
                    loadScheduleData(daysToLoad)
                  }
                }
              }}
              familyMembers={familyMembers}
              calendarEvents={schoolDistrictEvents}
            />
          ) : calendarView === 'agenda' ? (
            // Agenda/List View - Chronological list of events (starting from today)
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {dailySchedules
                .filter(day => {
                  // Filter to only show today and future dates
                  const today = extractDateInTimezoneSync(new Date().toISOString())
                  if (day.date < today) return false
                  // Also filter by selected kid if applicable
                  if (!selectedKid) return true
                  return day.presence[selectedKid]
                })
                .map((day) => (
                  <div key={day.date} className="bg-white rounded-lg p-3 border hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="text-lg">{getDayTypeEmoji(day.day_type)}</div>
                        <div>
                          <div className="font-medium">{formatDateLong(day.date)}</div>
                          <div className="text-sm text-gray-600">{getDayTypeLabel(day.day_type)}</div>
                        </div>
                      </div>
                      <div className="flex -space-x-2">
                        {day.kids_present.map(kid => {
                          const member = familyMembers.find(m => m.username === kid)
                          return member ? (
                            <div key={kid} className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm border-2 border-white">
                              {member.emoji}
                            </div>
                          ) : null
                        })}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            // Fallback - should not happen
            <div className="space-y-3">
              {schedulesWithFlags.map((day) => (
                <div key={day.date} className={`card border-2 ${getDayTypeColor(day.day_type)}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">{getDayTypeEmoji(day.day_type)}</div>
                      <div>
                        <div className="text-lg font-semibold text-gray-900">
                          {formatDateLong(day.date)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {getDayTypeLabel(day.day_type)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">
                        {day.total_kids_present}/4
                      </div>
                      <div className="text-xs text-gray-600">kids home</div>
                    </div>
                  </div>
                  
                  {/* Family member presence */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {familyMembers.map((member) => (
                      <div
                        key={member.username}
                        className={`flex items-center space-x-3 p-3 rounded-lg ${
                          day.presence[member.username]
                            ? 'bg-green-100 text-green-800 border border-green-200'
                            : 'bg-gray-100 text-gray-600 border border-gray-200'
                        }`}
                      >
                        <div className="text-2xl">{member.emoji}</div>
                        <div>
                          <div className="font-semibold">{member.display_name}</div>
                          <div className="text-sm flex items-center space-x-1">
                            <span>{getPresenceEmoji(day.presence[member.username])}</span>
                            <span>{getPresenceText(day.presence[member.username])}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Special indicators */}
                  {day.all_kids_present && (
                    <div className="mt-4 text-center bg-purple-100 text-purple-800 py-2 px-4 rounded-lg border border-purple-200">
                      🎉 <strong>All Kids Weekend</strong> - Everyone's together! Perfect for family activities.
                    </div>
                  )}
                  
                  {/* Google Calendar Events for this day */}
                  {googleCalendarEvents.filter(event => {
                    const eventDate = event.start.date || (event.start.dateTime ? extractDateInTimezoneSync(event.start.dateTime) : undefined)
                    return eventDate === day.date
                  }).length > 0 && (
                    <div className="mt-4 space-y-2">
                      <div className="text-sm font-semibold text-gray-700 flex items-center">
                        <span className="mr-2">📅</span>
                        Google Calendar Events
                      </div>
                      {googleCalendarEvents.filter(event => {
                        const eventDate = event.start.date || (event.start.dateTime ? extractDateInTimezoneSync(event.start.dateTime) : undefined)
                        return eventDate === day.date
                      }).map(event => (
                        <div key={event.id} className="p-2 bg-blue-50 rounded-lg border border-blue-200 text-sm">
                          <div className="font-medium text-blue-900">{event.summary}</div>
                          {event.start.dateTime && (
                            <div className="text-blue-700 text-xs">
                              {new Date(event.start.dateTime).toLocaleTimeString('en-US', { 
                                hour: 'numeric', 
                                minute: '2-digit' 
                              })}
                            </div>
                          )}
                          {event.description && (
                            <div className="text-blue-600 text-xs mt-1">{event.description}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Weekend Pattern Tab - Only show if custody tracking enabled AND not in demo mode AND there's actual custody data */}
      {showCustodySchedule && !isDemoMode && activeTab === 'patterns' && weekendPattern.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            🔄 Weekend Pattern Analysis
          </h3>

          <div className="mb-6 card bg-blue-50 border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-4 flex items-center">
              📋 Custody Schedule Rules
            </h3>
            
            {/* Changeover Times */}
            <div className="mb-4 p-3 bg-white rounded-lg border border-blue-200">
              <div className="text-sm text-blue-900">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-lg">🕕</span>
                  <strong>Changeover Times:</strong>
                </div>
                <div className="ml-7 space-y-1 text-blue-800">
                  <div>• <strong>Friday 6:00 PM:</strong> Weekend custody begins</div>
                  <div>• <strong>Sunday 6:00 PM:</strong> Weekend custody ends, weekday custody begins</div>
                </div>
              </div>
            </div>
            
          </div>
          
          <div className="space-y-4">
            {weekendPattern.map((weekend) => {
              const weekendType = getWeekendType(weekend)
              return (
              <div key={weekend.week} className={`card border-2 ${
                weekendType === 'primary'
                  ? 'border-blue-300 bg-blue-50'
                  : weekendType === 'other'
                  ? 'border-pink-300 bg-pink-50'
                  : 'border-yellow-300 bg-yellow-50'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                    <span className="text-xl">🗓️</span>
                    <span>Week {weekend.week}</span>
                    <span className="text-sm font-normal text-gray-600">
                      {formatDate(weekend.dates.saturday)} & {formatDate(weekend.dates.sunday)}
                    </span>
                  </h3>
                  <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                    getWeekendTypeStyle(weekend)
                  }`}>
                    {getWeekendTypeLabel(weekend)}
                  </div>
                </div>

                {/* Changeover Time Indicator */}
                <div className="mb-4 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-center text-sm text-amber-800">
                    <span className="font-medium">⏰ Weekend: Friday 6pm - Sunday 6pm</span>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Saturday */}
                  <div className="p-4 bg-white rounded-lg border">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <span className="mr-2">🌅</span>
                      Saturday - {formatDate(weekend.dates.saturday)}
                    </h4>
                    <div className="space-y-2">
                      {familyMembers.map((member) => (
                        <div
                          key={member.username}
                          className={`flex items-center justify-between p-2 rounded-lg ${
                            weekend.presence.saturday[member.username]
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-600'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <span>{member.emoji}</span>
                            <span className="font-medium">{member.display_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">
                              {weekend.presence.saturday[member.username] ? '🏠' : '✈️'}
                            </span>
                            <span className="text-xs font-medium">
                              {custodyLabel(custodyParents, weekend.presence.saturday[member.username] ? 'primary' : 'other')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Sunday */}
                  <div className="p-4 bg-white rounded-lg border">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <span className="mr-2">🌄</span>
                      Sunday - {formatDate(weekend.dates.sunday)}
                    </h4>
                    <div className="space-y-2">
                      {familyMembers.map((member) => (
                        <div
                          key={member.username}
                          className={`flex items-center justify-between p-2 rounded-lg ${
                            weekend.presence.sunday[member.username]
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-600'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <span>{member.emoji}</span>
                            <span className="font-medium">{member.display_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">
                              {weekend.presence.sunday[member.username] ? '🏠' : '✈️'}
                            </span>
                            <span className="text-xs font-medium">
                              {custodyLabel(custodyParents, weekend.presence.sunday[member.username] ? 'primary' : 'other')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {weekend.all_kids_present && (
                  <div className="mt-4 text-center bg-green-100 text-green-800 py-3 px-4 rounded-lg border border-green-200">
                    🎉 <strong>Perfect Family Weekend!</strong> All four kids will be together - 
                    ideal for group activities, family outings, and team chores.
                  </div>
                )}
              </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Summary Tab - Only show if custody tracking enabled AND not in demo mode */}
      {showCustodySchedule && !isDemoMode && activeTab === 'summary' && scheduleSummary && scheduleSummary.statistics && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">
            📊 Schedule Summary & Statistics
          </h3>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Statistics Cards */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 mb-4">📈 30-Day Statistics</h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="card bg-green-50 border-green-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {summaryStats.allKidsDays}
                    </div>
                    <div className="text-sm text-green-800">All Kids Days</div>
                    <div className="text-xs text-green-600 mt-1">
                      Perfect for family activities
                    </div>
                  </div>
                </div>

                <div className="card bg-gray-50 border-gray-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-600">
                      {scheduleSummary.statistics.no_kids_days || 0}
                    </div>
                    <div className="text-sm text-gray-800">Quiet Days</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Adult time / catch up
                    </div>
                  </div>
                </div>
                
                <div className="card bg-blue-50 border-blue-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {scheduleSummary.statistics.school_nights || 0}
                    </div>
                    <div className="text-sm text-blue-800">School Nights</div>
                    <div className="text-xs text-blue-600 mt-1">
                      Homework & routine
                    </div>
                  </div>
                </div>

                <div className="card bg-purple-50 border-purple-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {summaryStats.allKidsWeekendDays}
                    </div>
                    <div className="text-sm text-purple-800">All Kids Weekend Days</div>
                    <div className="text-xs text-purple-600 mt-1">
                      All together time
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Individual Child Statistics */}
              <div className="card">
                <h4 className="font-semibold text-gray-900 mb-3">👥 Individual Presence</h4>
                <div className="space-y-3">
                  {familyMembers.map((member) => {
                    const days = scheduleSummary.statistics.kid_days[member.username] || 0
                    const totalDays = scheduleSummary.summary_period.total_days
                    const percentage = totalDays > 0 ? Math.round((days / totalDays) * 100) : 0
                    
                    return (
                      <div key={member.username}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <span>{member.emoji}</span>
                            <span className="font-medium">{member.display_name}</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {days} days ({percentage}%)
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full"
                            style={{ 
                              width: `${percentage}%`,
                              backgroundColor: member.color
                            }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
            
            {/* Recent Schedule Preview */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">📅 This Week Preview</h3>
              <div className="space-y-3">
                {scheduleSummary.daily_schedules.map((day) => (
                  <div key={day.date} className={`p-3 rounded-lg border ${getDayTypeColor(day.day_type)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span>{getDayTypeEmoji(day.day_type)}</span>
                        <span className="font-medium">{formatDate(day.date)}</span>
                      </div>
                      <span className="text-sm">
                        {day.total_kids_present}/{familyMembers.length} home
                      </span>
                    </div>
                    <div className="text-xs text-gray-600">
                      Home: {day.kids_present.join(', ') || 'No one'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Events Tab */}
      {activeTab === 'events' && (
        <div className="card">
          <FamilyEvents />
        </div>
      )}
      
      {/* Availability Tab */}
      {activeTab === 'availability' && (
        <div className="card">
          {user?.is_parent ? (
            <ParentChildAvailabilityEnhanced onUpdate={loadScheduleData} />
          ) : (
            <AvailabilityCalendar />
          )}
        </div>
      )}
      
      {/* School Tab - Parents see management view */}
      {activeTab === 'school' && (
        <ParentSchoolManagement />
      )}
    </div>
  )
}